/**
 * Vivi Select
 *
 * Vsak <select class="vs-select"> nadgradi v dostopen dropdown (gumb + listbox),
 * animiran enako kot pill filtri na domači strani (grid-rows 0fr → 1fr).
 *
 * Varianti (razred na <select>):
 *  - .vs-select--sort  pill "Razvrsti po" (privzeto)
 *  - .vs-select--form  polje v obrazcu, videz kot .vivi-input
 *
 * Nativni <select> ostane v DOM (skrit, takoj pred ovojem .vs) in je edini vir resnice:
 * obstoječi moduli še naprej berejo $("select[name=order_select]").val() in poslušajo 'change'.
 * Ovoj je vstavljen kot SOSED (ne starš), zato $("#datum_od").parent() ostane ovoj polja (div.mb-4).
 *
 * Sinhronizacija z zunanjimi spremembami:
 *  - 'change' na <select>            → osveži napis
 *  - 'reset' na obrazcu              → osveži napis
 *  - $(select).trigger('vs:refresh') → po programski nastavitvi .val(x) brez dogodka
 *  - spremembe <option> (MutationObserver) → ponovno izriše seznam
 */

const CHEVRON = '<svg class="vs-chevron" width="12" height="7" viewBox="0 0 13.835 7.918" aria-hidden="true"><path d="M12.007,1,6.5,6.5,1,1" transform="translate(0.414 0.414)" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/></svg>'

const instances = []
let idSeq = 0

const closeAll = (except = null) => {
	instances.forEach((inst) => {
		if (inst !== except) inst.close()
	})
}

class ViviSelect {
	constructor(select) {
		this.select = select
		this.id = select.id ? `vs-${select.id}` : `vs-${++idSeq}`
		this.variant = select.classList.contains('vs-select--form') ? 'form' : 'sort'
		this.activeIndex = -1
		this.typeahead = ''
		this.typeaheadTimer = null

		this.build()
		this.bind()
		this.sync()
		instances.push(this)
	}

	/* ---------- DOM ---------- */

	build() {
		const s = this.select

		const wrap = document.createElement('div')
		wrap.className = `vs vs--${this.variant}`
		wrap.innerHTML =
			`<button type="button" class="vs-toggle" id="${this.id}-toggle" aria-haspopup="listbox" aria-expanded="false" aria-controls="${this.id}-list">` +
				'<span class="vs-label"></span>' + CHEVRON +
			'</button>' +
			'<div class="vs-panel"><div class="vs-panel-inner">' +
				`<ul class="vs-list" role="listbox" id="${this.id}-list" tabindex="-1" aria-labelledby="${this.id}-toggle"></ul>` +
			'</div></div>'

		s.insertAdjacentElement('afterend', wrap)

		// Nativni select skrijemo, a ostane funkcionalen za JS
		s.classList.add('hidden')
		s.setAttribute('aria-hidden', 'true')
		s.tabIndex = -1

		this.wrap = wrap
		this.toggle = wrap.querySelector('.vs-toggle')
		this.label = wrap.querySelector('.vs-label')
		this.list = wrap.querySelector('.vs-list')

		this.renderOptions()
	}

	renderOptions() {
		this.list.innerHTML = ''
		this.items = []

		Array.from(this.select.options).forEach((opt, index) => {
			// Sort pill: placeholder ("Razvrsti po") je že napis gumba, v seznamu ga ne ponavljamo
			if (opt.disabled && this.variant === 'sort') return

			const li = document.createElement('li')
			li.textContent = opt.textContent.trim()
			li.dataset.index = index

			if (opt.disabled) {
				// Placeholder ("Datum obiska") — neizbirljiv naslov seznama
				li.className = 'vs-option vs-option--placeholder'
				li.setAttribute('role', 'presentation')
			} else {
				li.className = 'vs-option'
				li.id = `${this.id}-opt-${index}`
				li.setAttribute('role', 'option')
				li.setAttribute('aria-selected', 'false')
				li.addEventListener('click', () => this.choose(index))
				// Označevanje pod kazalcem samo za miško — na dotiku bi utripalo med drsenjem seznama
				li.addEventListener('pointermove', (e) => {
					if (e.pointerType === 'mouse') this.setActive(index, false)
				})
			}

			this.list.appendChild(li)
			this.items[index] = li
		})
	}

	/* ---------- stanje ---------- */

	get isOpen() {
		return this.wrap.classList.contains('is-open')
	}

	get selectableIndexes() {
		return Array.from(this.select.options)
			.map((opt, i) => (opt.disabled ? -1 : i))
			.filter((i) => i >= 0)
	}

	// Prenese stanje iz nativnega selecta v gumb in seznam
	sync() {
		const s = this.select
		const opt = s.options[s.selectedIndex]

		// Sort pill vedno kaže naslov ("Razvrsti po"), ne izbrane možnosti
		const placeholder = this.variant === 'sort'
			? Array.from(s.options).find((o) => o.disabled)
			: null
		this.label.textContent = placeholder
			? placeholder.textContent.trim()
			: (opt ? opt.textContent.trim() : '')
		this.wrap.classList.toggle('vs--placeholder', !opt || opt.disabled)
		this.wrap.classList.toggle('vs--disabled', s.disabled)
		this.toggle.disabled = s.disabled

		this.items.forEach((li, i) => {
			if (li.getAttribute('role') !== 'option') return
			const selected = i === s.selectedIndex
			li.classList.toggle('is-selected', selected)
			li.setAttribute('aria-selected', selected ? 'true' : 'false')
		})
	}

	choose(index) {
		const s = this.select
		if (s.selectedIndex !== index) {
			s.selectedIndex = index
			s.dispatchEvent(new Event('change', { bubbles: true }))
		}
		this.sync()
		this.close()
		this.toggle.focus({ preventScroll: true })
	}

	setActive(index, scroll = true) {
		if (this.activeIndex >= 0 && this.items[this.activeIndex]) {
			this.items[this.activeIndex].classList.remove('is-active')
		}
		this.activeIndex = index
		const li = this.items[index]
		if (!li) {
			this.toggle.removeAttribute('aria-activedescendant')
			return
		}
		li.classList.add('is-active')
		this.toggle.setAttribute('aria-activedescendant', li.id)
		if (scroll) this.scrollIntoList(li)
	}

	// Drsi samo znotraj seznama (ne scrollIntoView — ta bi ob še zaprtem panelu premaknil celo stran)
	scrollIntoList(li) {
		const list = this.list
		if (list.scrollHeight <= list.clientHeight) return
		const top = li.offsetTop - list.offsetTop
		const bottom = top + li.offsetHeight
		if (top < list.scrollTop) list.scrollTop = top
		else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight
	}

	moveActive(step) {
		const idx = this.selectableIndexes
		if (!idx.length) return
		const pos = idx.indexOf(this.activeIndex)
		let next
		if (pos === -1) next = step > 0 ? idx[0] : idx[idx.length - 1]
		else next = idx[Math.min(Math.max(pos + step, 0), idx.length - 1)]
		this.setActive(next)
	}

	open() {
		if (this.isOpen || this.select.disabled) return
		this.sync() // ujame morebitne pozne programske spremembe
		closeAll(this)
		this.wrap.classList.add('is-open')
		this.toggle.setAttribute('aria-expanded', 'true')

		const s = this.select
		const start = s.options[s.selectedIndex] && !s.options[s.selectedIndex].disabled
			? s.selectedIndex
			: (this.selectableIndexes[0] ?? -1)
		this.setActive(start)
	}

	close() {
		if (!this.isOpen) return
		this.wrap.classList.remove('is-open')
		this.toggle.setAttribute('aria-expanded', 'false')
		this.setActive(-1, false)
	}

	/* ---------- dogodki ---------- */

	bind() {
		const s = this.select

		this.toggle.addEventListener('click', () => (this.isOpen ? this.close() : this.open()))
		this.toggle.addEventListener('keydown', (e) => this.onKeydown(e))

		// Tab / fokus izven komponente zapre seznam
		this.wrap.addEventListener('focusout', (e) => {
			if (!e.relatedTarget || !this.wrap.contains(e.relatedTarget)) this.close()
		})

		// Zunanje spremembe nativnega selecta
		s.addEventListener('change', () => this.sync())
		s.addEventListener('vs:refresh', () => this.sync())
		if (s.form) s.form.addEventListener('reset', () => setTimeout(() => this.sync(), 0))

		// Dinamično spremenjene <option>
		new MutationObserver(() => {
			this.renderOptions()
			this.sync()
		}).observe(s, { childList: true, subtree: true, characterData: true })
	}

	onKeydown(e) {
		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault()
				if (!this.isOpen) this.open()
				else this.moveActive(1)
				break
			case 'ArrowUp':
				e.preventDefault()
				if (!this.isOpen) this.open()
				else this.moveActive(-1)
				break
			case 'Home':
				if (!this.isOpen) return
				e.preventDefault()
				this.setActive(this.selectableIndexes[0] ?? -1)
				break
			case 'End':
				if (!this.isOpen) return
				e.preventDefault()
				this.setActive(this.selectableIndexes.at(-1) ?? -1)
				break
			case 'Enter':
			case ' ':
				e.preventDefault()
				if (!this.isOpen) this.open()
				else if (this.activeIndex >= 0) this.choose(this.activeIndex)
				break
			case 'Escape':
				if (!this.isOpen) return
				e.preventDefault()
				this.close()
				break
			case 'Tab':
				this.close()
				break
			default:
				// Iskanje s tipkanjem (prve črke)
				if (e.key.length === 1 && !e.altKey && !e.ctrlKey && !e.metaKey) {
					this.typeahead += e.key.toLowerCase()
					clearTimeout(this.typeaheadTimer)
					this.typeaheadTimer = setTimeout(() => { this.typeahead = '' }, 600)

					const match = this.selectableIndexes.find((i) =>
						this.select.options[i].textContent.trim().toLowerCase().startsWith(this.typeahead)
					)
					if (match === undefined) return
					if (this.isOpen) this.setActive(match)
					else this.choose(match)
				}
		}
	}
}

/* ---------- globalno ---------- */

let globalBound = false
const bindGlobal = () => {
	if (globalBound) return
	globalBound = true

	document.addEventListener('pointerdown', (e) => {
		instances.forEach((inst) => {
			if (inst.isOpen && !inst.wrap.contains(e.target)) inst.close()
		})
	})

	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape') closeAll()
	})
}

export default {
	init(root = document) {
		root.querySelectorAll('select.vs-select').forEach((select) => {
			if (!select.viviSelect) select.viviSelect = new ViviSelect(select)
		})
		bindGlobal()
	}
}
